import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Fab,
  Paper,
  Alert,
  Link,
  Skeleton,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  KeyboardArrowUp as ArrowUpIcon,
  ImageOutlined as ImageOutlinedIcon,
  FileDownload as DownloadIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { parseVariation, parsePlatformContent, extractGeneratedContent } from './utils/contentParser';
import { PlatformIcon } from '../../components/features/content/PlatformIcon';
import { VariationCard } from '../../components/features/content/VariationCard';
import { hexToRgba } from './utils/colorUtils';
import { getPlatformDisplayName } from '../../utils/platformUtils';

interface GeneratedContentProps {
  generatedContent: any;
  copiedStates: { [key: string]: boolean };
  showScrollToTop: boolean;
  onCopy: (text: string, key: string) => void;
  onRegenerate: () => void;
  onScrollToTop: () => void;
}

// ── AI Image Card ─────────────────────────────────────────────────────────────

interface GeneratedImageCardProps {
  imageStatus?: string | null;
  imageUrl?: string | null;
  imagePrompt?: string | null;
  imageErrorMessage?: string | null;
}

const GeneratedImageCard: React.FC<GeneratedImageCardProps> = ({
  imageStatus,
  imageUrl,
  imagePrompt,
  imageErrorMessage,
}) => {
  const { t } = useTranslation('content');
  const [showPrompt, setShowPrompt] = useState(false);

  const handleDownload = async (url: string) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = 'bizcontent-image.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(url, '_blank');
    }
  };

  // Still processing — show skeleton while background task runs
  if (!imageStatus) {
    return (
      <Card variant="outlined" sx={{ mb: 3, borderColor: 'primary.light', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageOutlinedIcon sx={{ fontSize: 18 }} />
            {t('generation.image.cardTitle')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Skeleton variant="rectangular" sx={{ width: '100%', maxWidth: 400, height: 280, borderRadius: 2 }} />
          </Box>
          <Skeleton width="35%" height={32} sx={{ mb: 0.5 }} />
          <Skeleton width="55%" height={16} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ mb: 3, borderColor: 'primary.light', borderRadius: 2 }}>
      <CardContent>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ImageOutlinedIcon sx={{ fontSize: 18 }} />
          {t('generation.image.cardTitle')}
        </Typography>

        {/* SUCCESS */}
        {imageStatus === 'success' && imageUrl && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box
                component="img"
                src={imageUrl}
                alt={t('generation.image.cardTitle')}
                sx={{
                  maxWidth: 400,
                  width: '100%',
                  borderRadius: 2,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownload(imageUrl!)}
              >
                {t('generation.image.download')}
              </Button>
            </Box>

            {imagePrompt && (
              <Box sx={{ mt: 1 }}>
                <Link
                  component="button"
                  variant="caption"
                  color="text.secondary"
                  onClick={() => setShowPrompt(prev => !prev)}
                  sx={{ cursor: 'pointer' }}
                >
                  {showPrompt ? t('generation.image.hidePrompt') : t('generation.image.showPrompt')}
                </Link>
                {showPrompt && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}
                  >
                    {imagePrompt}
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}

        {/* FAILED */}
        {imageStatus === 'failed' && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {t('generation.image.failed')}
          </Alert>
        )}

        {/* SKIPPED */}
        {imageStatus === 'skipped' && (
          <Alert severity="info" sx={{ mt: 1 }}>
            {imageErrorMessage || t('generation.image.skipped')}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

const GeneratedContent: React.FC<GeneratedContentProps> = ({
  generatedContent,
  copiedStates,
  showScrollToTop,
  onCopy,
  onRegenerate,
  onScrollToTop,
}) => {
  const { t } = useTranslation('content');
  
  // Extract the actual generated_content from the API response
  const { content, context: topLevelContext } = extractGeneratedContent(generatedContent);

  if (!content || (typeof content === 'object' && Object.keys(content).length === 0)) {
    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="body1" color="text.secondary">
            {t('generation.noResult')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Use website's primary color for all platform cards
  const getPlatformColor = (): string => {
    return '#6C63FF'; // Website primary color
  };

  // Render platform card
  const renderPlatformCard = (platform: string, platformContent: any, globalContext?: string) => {
    const platformKey = platform.toLowerCase().replace(/\s+/g, '_');
    
    // If platformContent is the entire content structure (has platform keys), extract the specific platform
    if (platformContent && typeof platformContent === 'object' && platformContent !== null) {
      const platformKeys = ['facebook', 'linkedin', 'instagram', 'twitter', 'youtube', 'pinterest', 'email', 'blog', 'tiktok'];
      const hasPlatformKeys = Object.keys(platformContent).some(key => platformKeys.includes(key.toLowerCase()));
      
      if (hasPlatformKeys) {
        // Find the matching platform key (case-insensitive)
        const matchingKey = Object.keys(platformContent).find(key => 
          key.toLowerCase() === platformKey || key.toLowerCase() === platform.toLowerCase()
        );
        
        if (matchingKey && platformContent[matchingKey]) {
          platformContent = platformContent[matchingKey];
        }
      }
    }
    
    // Parse platform content using utility function - pass platformKey to help with parsing
    const parsed = parsePlatformContent(platformContent, platformKey);
    const { context: platformContext, variations } = parsed;
    
    // Use platform context if available, otherwise use global context
    const displayContext = platformContext || globalContext;

    // Get website's primary color and create light background
    const platformColor = getPlatformColor();
    const lightBackgroundColor = hexToRgba(platformColor, 0.08);

    return (
      <Card
        key={platform}
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          backgroundColor: lightBackgroundColor,
          borderLeft: `4px solid ${platformColor}`,
        }}
      >
        <CardContent>
          {/* Platform Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PlatformIcon platformName={platform} />
              <Typography variant="h6" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                {getPlatformDisplayName(platform)}
              </Typography>
            </Box>
            <Tooltip title={t('generation.copyAll')}>
              <IconButton
                size="small"
                onClick={() => {
                  const allText = variations
                    .map((v, idx) => `Variation ${idx + 1}:\n${parseVariation(v.data).content}`)
                    .join('\n\n');
                  onCopy(allText, `${platformKey}_all`);
                }}
                color={copiedStates[`${platformKey}_all`] ? 'success' : 'default'}
              >
                {copiedStates[`${platformKey}_all`] ? <CheckIcon /> : <CopyIcon />}
              </IconButton>
            </Tooltip>
          </Box>

          {/* Context Paragraph - Display before variations */}
          {displayContext && (
            <Paper
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.6,
                  color: 'text.secondary',
                }}
              >
                {displayContext}
              </Typography>
            </Paper>
          )}

          {/* Variations Grid - Side by Side */}
          <Grid container spacing={2}>
            {variations.map((variation) => (
              <Grid size={{ xs: 12, md: 6 }} key={variation.key}>
                <VariationCard
                  variation={variation.data}
                  variationKey={variation.key}
                  platformKey={platformKey}
                  variationIndex={variation.index}
                  copiedStates={copiedStates}
                  onCopy={onCopy}
                />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ mt: 4 }}>
      {/* Header with Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t('generation.result')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onRegenerate}
          >
            {t('generation.regenerate')}
          </Button>
        </Box>
      </Box>

      {/* AI Image Card — shown above platform cards when image was requested */}
      {generatedContent?.generate_image && (
        <GeneratedImageCard
          imageStatus={generatedContent?.image_status}
          imageUrl={generatedContent?.image_url}
          imagePrompt={generatedContent?.image_prompt}
          imageErrorMessage={generatedContent?.image_error_message}
        />
      )}

      {/* Platform Cards */}
      <Box>
        {Object.entries(content).map(([platform, platformContent]) =>
          renderPlatformCard(platform, platformContent, topLevelContext)
        )}
      </Box>
    </Box>
  );
};

// Scroll to top FAB - rendered outside the card
export const ScrollToTopFab: React.FC<{ show: boolean; onClick: () => void }> = ({ show, onClick }) => {
  if (!show) return null;
  
  return (
    <Fab
      color="primary"
      aria-label="scroll to top"
      onClick={onClick}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 1000,
      }}
    >
      <ArrowUpIcon />
    </Fab>
  );
};

export default GeneratedContent;
